import { useState } from 'react';
import { UserPlus, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import useAuthStore from '@/store/useAuthStore';
import useFriendsStore from '@/store/useFriendsStore';
import Portal from '@/components/ui/Portal';

interface AddFriendButtonProps {
    userId?: string;
    compact?: boolean;
    className?: string;
    onSuccess?: () => void;
}

export default function AddFriendButton({
    className = '',
    onSuccess
}: AddFriendButtonProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState(''); // Uncommented to store error messages

    const { isAuthenticated } = useAuthStore(); // user
    const { sendFriendRequest } = useFriendsStore();

    // Handle friend request by email or username input
    const handleIdentifierSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!inputValue.trim() || !isAuthenticated) return;

        setStatus('loading');
        setErrorMessage('');

        const isEmail = inputValue.includes('@');
        const payload = isEmail ? { email: inputValue } : { username: inputValue };
        const result = await sendFriendRequest(payload);
 
        if (result.success) {
            setStatus('success');
            setIsOpen(false);
            setInputValue('');
            if (onSuccess) {
                onSuccess();
            }
            setTimeout(() => setStatus('idle'), 2000);
        } else {
            setStatus('error');
            setErrorMessage(result.error || 'Failed to send request. Please check the email/username and try again.');
            // Keep modal open on error to show message or allow retry
            // No need to set timeout to clear status here if we want the error to persist until user action
        }
    };

    // If not authenticated, redirect to login
    const handleClick = () => {
        if (!isAuthenticated) {
            window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
            return;
        }
        setIsOpen(true);
    };

    return (
        <>
            <motion.button
                type="button"
                onClick={handleClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`group relative overflow-hidden px-4 py-2 rounded-lg flex items-center space-x-2 bg-[rgb(var(--accent-1))]/20 hover:bg-[rgb(var(--accent-1))]/30 border border-[rgb(var(--accent-1))]/40 shadow-xs text-sm font-medium text-white ${className}`}
                disabled={status === 'loading'}
            >
                {/* hover sweep removed per request */}
                <UserPlus className="h-5 w-5 mr-2" />
                Add Friend
            </motion.button>

            {isOpen && (
                <Portal>
                    <div
                        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4"
                        onClick={() => { if (status !== 'loading') setIsOpen(false); }} // Prevent closing while loading
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '1rem'
                        }}
                    >
                        <div
                            className="bg-zinc-800/90 rounded-xl shadow-lg border border-zinc-700/50 w-full max-w-sm"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                position: 'relative',
                                margin: 'auto'
                            }}
                        >
                            <div className="px-6 pt-6 pb-4">
                                <h2 className="text-xl font-light tracking-wide text-white mb-4">Add Friend</h2>
                                <p className="text-zinc-300 mb-6">
                                    Send a friend request by entering their email or username below.
                                </p>

                                <form onSubmit={handleIdentifierSubmit}>
                                    <div className="mb-4"> {/* Reduced mb */}
                                        <label htmlFor="friendIdentifier" className="block text-sm text-zinc-400 mb-2">
                                            Friend's Email or Username
                                        </label>
                                        <input
                                            type="text"
                                            id="friendIdentifier"
                                            value={inputValue}
                                            onChange={(e) => setInputValue(e.target.value)}
                                            className="w-full bg-zinc-800/50 border border-zinc-700 rounded-xl px-4 py-3 text-white focus:ring-[rgb(var(--accent-1))]/50 focus:border-[rgb(var(--accent-1))]/50"
                                            placeholder="Enter email or username"
                                            required
                                            disabled={status === 'loading'}
                                        />
                                    </div>

                                    {status === 'error' && errorMessage && (
                                        <p className="text-red-500 text-sm mb-4">{errorMessage}</p>
                                    )}

                                    <div className="flex gap-3 justify-end">
                                        <button
                                            type="button"
                                            onClick={() => { if (status !== 'loading') setIsOpen(false); }}
                                            className="px-6 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700"
                                            disabled={status === 'loading'}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-6 py-2.5 rounded-xl font-medium bg-[rgb(var(--accent-1))] hover:opacity-90 flex items-center"
                                            disabled={status === 'loading' || !inputValue.trim()}
                                        >
                                            {status === 'loading' ? (
                                                <>
                                                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                                    Sending...
                                                </>
                                            ) : (
                                                <>
                                                    <UserPlus className="h-5 w-5 mr-2" />
                                                    Send Request
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </Portal>
            )}
        </>
    );
}